import { line, select, Selection, symbol, symbolCircle } from 'd3';
import { LineChartConfig } from '../data/LineChartConfig';
import { BarChartConfig } from '../data/BarChartConfig';
import { ChartDataArray } from '../data/ChartDataArray';
import { ChartDataType } from '../data/ChartDataType';
import { LegendData } from './LegendData';
import { ChartRendererContext } from './ChartRendererContext';
import { Size } from './Size';
import { ChartDataComparisonType } from '../data/ChartDataComparisonType';
import { BandChartConfig } from 'components/chart/data/BandChartConfig';

export class LegendRenderer {
  constructor(
    private context: ChartRendererContext,
    private config: LineChartConfig | BarChartConfig
  ) {}

  public render(
    svg: Selection<SVGSVGElement, unknown, null, undefined>,
    data: ChartDataArray[],
    chartSize: Size = { width: 0, height: 0 }
  ): number {
    const legendData: LegendData[] = this.createLegendData(data);

    if (legendData.length <= 1) {
      return 0;
    }

    const iconWidth = 16;
    const iconMargin = 6;

    const itemsPerLine = Math.max(data.length, 5);

    const width = chartSize.width - 1;

    // hint: this approach is flawd since there might be some items significantly
    // smaller than 120 and therefor there is more space for others - but
    // i have no time at the moment to calculate this...
    const maxNodeLength = Math.max(chartSize.width - 1 / itemsPerLine, 120);
    // subtract with of the legend symbol
    const maxTextLength = maxNodeLength - iconWidth - iconMargin;
    const isBreakdownLegend = data.filter(series => series.type === ChartDataType.breakdown).length > 0;

    const gElement = svg.append('g').attr('class', 'legend-container');

    const legend = gElement
      .selectAll('legend')
      .data(legendData)
      .enter()
      .append('g')
      .attr('class', leg => {
        let legendClass = 'legend';
        if (leg.comparisonType === ChartDataComparisonType.comparison) {
          legendClass += ' comparison';
        }
        if (leg.dataType === ChartDataType.secondary) {
          legendClass += ' secondary';
        }
        return legendClass;
      });

    legend
      .append('path')
      .attr('class', leg => 'legend-icon ' + (leg.className || ''))
      .style('stroke', leg => (leg.iconType === 'dot' ? 'none' : (leg.color ?? null)))
      .style('fill', leg => (leg.iconType === 'dot' ? (leg.color ?? null) : null))
      .style('stroke-width', leg => {
        if (leg.iconType === 'dot') return null;
        if (leg.className && leg.className.includes('bar')) return null;
        return '2px';
      })
      .attr('width', 12)
      .attr('height', 8);

    const lineHeight = 14;
    const textMargin = 12;

    let currentX = 0;
    let currentY = 0;

    let legendWidth = 0;

    const xy: { x: number; y: number }[] = [];

    const drawLine = line<{ x: number; y: number }>()
      .x(n => n.x)
      .y(n => n.y);

    const dotIcon = symbol().type(symbolCircle).size(64);

    legend
      .append('text')
      .text(leg => leg.label)
      .each((_d, i, nodes) => {
        if (isBreakdownLegend) {
          // cut off too long labels
          const node = select(nodes[i]);
          while (node.node()!.getComputedTextLength() > maxTextLength) {
            node.text(node.text().slice(0, -4));
            node.text(`${node.text()}...`);
          }
        }
      })
      .attr('y', (_d, i, nodes) => {
        const hasLabel = legendData[i].label;
        const textWidth = nodes[i].getBoundingClientRect().width;
        const nodeWidth = textWidth + iconMargin + iconWidth + (hasLabel ? textMargin : 0);

        // if there is no legend we 'combine' this item with the next one
        const combinedWidth =
          !hasLabel && nodes.length > i + 1
            ? nodes[i + 1].getBoundingClientRect().width + iconWidth + iconMargin + textMargin + nodeWidth
            : nodeWidth;

        if (currentX + combinedWidth > width) {
          currentX = 0;
          currentY += lineHeight;
        }

        xy.push({ x: currentX, y: currentY });

        currentX += nodeWidth;

        if (currentY === 0) {
          legendWidth += nodeWidth;
        }

        return currentY;
      })
      .attr('x', (_d, i, nodes) => {
        const rect = nodes[i].parentElement!.querySelector('path');
        const xLeft = xy[i].x;
        const xRight = xLeft + iconWidth;
        const y = xy[i].y - 4;

        if (legendData[i]?.iconType === 'dot') {
          select(rect)
            .attr('d', dotIcon())
            .attr('transform', `translate(${xLeft + iconWidth / 2}, ${y})`);
        } else {
          select(rect)
            .attr(
              'd',
              drawLine([
                { x: xLeft, y },
                { x: xRight, y },
              ])
            )
            .attr('transform', null);
        }

        return xLeft + iconWidth + iconMargin;
      });

    let xOffset = width / 2 - legendWidth / 2;
    legend.attr('transform', 'translate(' + xOffset + ',' + 0 + ')');

    const legendHeight = xy.map(v => v.y).reduce((a, b) => Math.max(a, b), 0) + lineHeight + 3;
    gElement.attr('transform', 'translate(' + 0 + ',' + (chartSize.height - 3) + ')');
    return legendHeight;
  }

  private createLegendData(data: ChartDataArray[]): LegendData[] {
    const hasSecondaryComparison =
      data.filter(series => series.type === ChartDataType.secondary && series.isComparison).length > 0;
    const hasSecondaryLegends =
      data.find(series => series.type === ChartDataType.secondary) &&
      (this.config instanceof LineChartConfig || this.config.secondaryAsLine);

    let displayData;
    if (this.config.breakdownMode) {
      displayData = data.filter(
        series => series.type === ChartDataType.breakdown || series.type === ChartDataType.secondary
      );
    } else {
      displayData = data
        .filter(
          series =>
            series.type !== ChartDataType.average &&
            (series.type !== ChartDataType.secondary || (!series.isComparison && !series.isPlan))
        )
        .sort((a, b) => {
          const sortIndexA = LegendRenderer.getSortIndex(a);
          const sortIndexB = LegendRenderer.getSortIndex(b);
          return sortIndexA - sortIndexB;
        });
    }

    const legendData: LegendData[] = [];

    displayData.forEach((ele: ChartDataArray, idx: number) => {
      const legendEle = new LegendData(this.context.attributeFormatter(ele.label) ?? null, '');

      let secondaryLegendEle: LegendData | null = null;

      legendEle.color = ele.color;
      legendEle.className = `chart-stroke-color-${idx}`;

      legendEle.dataType = ele.type;
      legendEle.comparisonType = ele.comparisonType;

      if (ele.isComparison || ele.isPlan) {
        legendEle.className = ele.isComparison ? 'comparison-series' : 'plan-series';

        if (hasSecondaryComparison || this.config instanceof BarChartConfig) {
          if (hasSecondaryLegends && !(this.config as BarChartConfig).secondaryAsLine) {
            legendEle.className += ele.isComparison
              ? ' chart-stroke-color-light-green'
              : ' chart-stroke-color-lighter-green';
          } else {
            legendEle.className += ' bar-comparison';
          }
        }

        if (hasSecondaryLegends) {
          secondaryLegendEle = new LegendData(legendEle.label, ele.isComparison ? 'comparison-series' : 'plan-series');
          secondaryLegendEle.className += ele.isComparison
            ? ' chart-stroke-color-light-purple'
            : ' chart-stroke-color-lighter-purple';
          secondaryLegendEle.comparisonType = ChartDataComparisonType.comparison;
          secondaryLegendEle.dataType = ChartDataType.secondary;
          legendEle.label = null;
        }
      }

      if (ele.dashed) {
        legendEle.className = `${legendEle.className} dashed`;
      }

      if (
        this.config instanceof BarChartConfig &&
        !(this.config.secondaryAsLine && ele.type === ChartDataType.secondary)
      ) {
        legendEle.className += ' bar';
      } else if (
        this.config instanceof BarChartConfig &&
        this.config.secondaryAsLine &&
        ele.type === ChartDataType.secondary
      ) {
        legendEle.iconType = 'line';
      }

      legendData.push(legendEle);

      if (secondaryLegendEle) {
        legendData.push(secondaryLegendEle);
      }
      if (this.config.breakdownMode === 'band') {
        legendData.push(new LegendData((this.config as BandChartConfig).bandLabel ?? '', 'band-legend'));
      }
    });

    if (this.config.detectionDateMarker) {
      const detectionDateLegend = new LegendData(this.config.detectionDateMarker.label, 'detection-date', undefined);
      detectionDateLegend.color = this.config.detectionDateMarker.color;
      detectionDateLegend.iconType = 'dot';
      legendData.push(detectionDateLegend);
    }

    return legendData;
  }

  private static getSortIndex(array: ChartDataArray): number {
    if (array.isPlan) {
      return 3;
    }
    if (array.isComparison) {
      return 2;
    }

    return array.type === ChartDataType.primary ? 0 : 1;
  }
}
